import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getProjectWorkspace } from "@/app/actions/project/workspace/action";
import { ProjectWorkspace } from "@/app/types/project/ProjectWorkspace";
import { QueryKey } from "@/app/vars/QueryKeys";

type Options = {
  projectId?: number;
  enabled?: boolean;
  initialData?: ProjectWorkspace;
};

export function useFetchProjectWorkspace(options: Options) {
  const { projectId, enabled = true, initialData } = options;
  const queryClient = useQueryClient();

  const seedCaches = (workspace: ProjectWorkspace) => {
    queryClient.setQueryData([QueryKey.PROJECT_MEMBERS, projectId], workspace.members);
    queryClient.setQueryData([QueryKey.PROJECT_OVERVIEW, projectId], workspace.overview);
  };

  const seededProjectId = useRef<number | undefined>(undefined);
  if (initialData && projectId && seededProjectId.current !== projectId) {
    seedCaches(initialData);
    seededProjectId.current = projectId;
  }

  return useQuery({
    queryKey: [QueryKey.PROJECT_WORKSPACE, projectId],
    queryFn: async () => {
      const workspace = await getProjectWorkspace(projectId!);
      seedCaches(workspace);

      return workspace;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededProjectId.current === projectId ? initialData : undefined,
  });
}
